import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  deleteCrossClientLandingExecution,
  setCrossClientLandingDecompositionResult,
  setCrossClientInitLoading,
} from '../../../store/crossClient/crossClientLanding'
import {
  type CrossClientQueryStartPayload,
  useDecomposeCrossClientQueryMutation,
  useStartCrossClientQueryMutation,
} from '../../../store/crossClient/crossClientExecution'
import { useDeleteCrossClientQueryExecutionMutation } from '../../../store/crossClient/crossClient'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { CROSS_CLIENT_FUNDS_TOAST_MESSAGE, isCrossClientFundsQuotaError } from '../utils/crossClientFunds'
import { type CrossClientStartedQuery, justStartedState } from '../utils'
import { ccqCriteria, ccqCriterionClauses } from '../Results/Run/ccqResultView'
import {
  type CrossClientQueryAtomicClause,
  type CrossClientQueryDecompositionOutcome,
  CrossClientQueryDecompositionOutcomes,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryExecution,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQuerySuggestedDecomposition,
  type PredefinedCrossClientQuery,
} from '../../../models/CrossClient'
import { isRecognisedOutcome, isUnusableDecomposition } from './decompositionGate'
import { type DecompositionContext, type QueryConfirmedEventInput, decompositionEvent, queryConfirmedEvent } from './decompositionAnalytics'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking'

interface DecomposeArgsBase {
  userQuestion: string
  sources: CrossClientQueryDataSource[]
  clientQueryMode: CrossClientQueryClientQueryMode
}

interface CustomDecomposeArgs extends DecomposeArgsBase {
  predefinedQueryId?: never
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

interface PredefinedDecomposeArgs extends DecomposeArgsBase {
  predefinedQueryId: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

type DecomposeArgs = CustomDecomposeArgs | PredefinedDecomposeArgs

interface ConfirmStartSettings {
  sources: CrossClientQueryDataSource[]
  clientQueryMode: CrossClientQueryClientQueryMode
}

export const MAX_USER_QUESTION_LENGTH = 350

interface ReviewCommonFields {
  atomicClauses: CrossClientQueryAtomicClause[]
  sources: CrossClientQueryDataSource[]
}

interface StartFields extends ReviewCommonFields {
  question: string
}

export type CrossClientReviewState =
  | ({ kind: 'confirm'; payload: CrossClientQueryStartPayload; confirmed: Omit<QueryConfirmedEventInput, 'context'> } & ReviewCommonFields)
  | ({ kind: 'trigger-failed'; payload: CrossClientQueryStartPayload; question: string } & ReviewCommonFields)
  | {
      kind: 'rejected'
      message: string
      atomicClauses: CrossClientQueryAtomicClause[]
      sources: CrossClientQueryDataSource[]
      outcome?: CrossClientQueryDecompositionOutcome
      suggestion?: CrossClientQuerySuggestedDecomposition
    }
  | {
      kind: 'refinement'
      atomicClauses: CrossClientQueryAtomicClause[]
      sources: CrossClientQueryDataSource[]
      suggestion: CrossClientQuerySuggestedDecomposition
      decomposition: CrossClientQueryLogicalQuestionDecomposition
      nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
    }

export const CROSS_CLIENT_DECOMPOSITION_REJECTED_FALLBACK =
  "I couldn't identify specific criteria from your question. Please add more details so I can search your clients effectively."

// Find clients on a decomposed question's confirm card, and Retry, start with the sources/clients the question was
// submitted with (only a curated query takes the pills' current values), so the pills can't change anything there.
export const reviewKeepsSubmittedSettings = (reviewState: CrossClientReviewState | null): boolean =>
  reviewState?.kind === 'trigger-failed' || (reviewState?.kind === 'confirm' && reviewState.payload.predefinedQueryId === undefined)

export const useCrossClientQueryLandingExecution = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const [triggerDecomposeCrossClientQuery] = useDecomposeCrossClientQueryMutation()
  const [triggerStartCrossClientQuery] = useStartCrossClientQueryMutation()

  const [reviewState, setReviewState] = useState<CrossClientReviewState | null>(null)
  const [startingQuery, setStartingQuery] = useState<CrossClientStartedQuery | null>(null)
  const decompositionContextRef = useRef<DecompositionContext | null>(null)
  const confirmedEventRef = useRef<Omit<QueryConfirmedEventInput, 'context'> | null>(null)

  const trackConfirmed = useCallback(() => {
    const confirmed = confirmedEventRef.current
    if (confirmed) {
      trackingClient.track(queryConfirmedEvent({ ...confirmed, context: decompositionContextRef.current }))
    }
  }, [trackingClient])

  const openResults = useCallback(
    (executionId: string, startedQuery: CrossClientStartedQuery) => {
      navigate(`/cross-client/executions/${executionId}`, { replace: true, state: justStartedState(startedQuery) })
    },
    [navigate],
  )

  const resetExecution = useCallback(() => {
    dispatch(setCrossClientLandingDecompositionResult(null))
    setReviewState(null)
    decompositionContextRef.current = null
  }, [dispatch])

  // Each decompose run gets an id; cancelling bumps it so a late response (or its loading flag) can't resurface.
  const decomposeRunRef = useRef(0)
  const decomposeRequestRef = useRef<{ abort: () => void } | null>(null)

  const withInitLoading = useCallback(
    async (work: (isCurrentRun: () => boolean) => Promise<void>) => {
      decomposeRunRef.current += 1
      const runId = decomposeRunRef.current
      const isCurrentRun = () => decomposeRunRef.current === runId
      dispatch(setCrossClientInitLoading(true))
      try {
        await work(isCurrentRun)
      } finally {
        if (isCurrentRun()) {
          dispatch(setCrossClientInitLoading(false))
        }
      }
    },
    [dispatch],
  )

  const cancelDecomposition = useCallback(() => {
    decomposeRunRef.current += 1
    decomposeRequestRef.current?.abort()
    decomposeRequestRef.current = null
    dispatch(setCrossClientInitLoading(false))
  }, [dispatch])

  useEffect(() => cancelDecomposition, [cancelDecomposition])

  // The landing shows the results page's init screen from the click on, so starting never shows a loader of its own.
  const startCrossClientQuery = useCallback(
    async (payload: CrossClientQueryStartPayload, { question, ...common }: StartFields) => {
      const startedQuery = { question, criteria: ccqCriteria(common.atomicClauses), shownSince: Date.now() }
      setStartingQuery(startedQuery)
      try {
        const { executionId } = await triggerStartCrossClientQuery({ accountId, payload }).unwrap()
        trackConfirmed()
        openResults(executionId, startedQuery)
      } catch (e) {
        setStartingQuery(null)
        if (isCrossClientFundsQuotaError(e)) {
          dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
          resetExecution()
          return
        }
        console.error(e)
        setReviewState({ kind: 'trigger-failed', payload, question, ...common })
      }
    },
    [accountId, dispatch, triggerStartCrossClientQuery, trackConfirmed, openResults, resetExecution],
  )

  const decomposeCrossClientQuery = useCallback(
    (args: DecomposeArgs) =>
      withInitLoading(async (isCurrentRun) => {
        const didDecompose = !args.decomposition
        let decomposition = args.decomposition
        let nnfDecomposition = args.nnfDecomposition
        let suggestion: CrossClientQuerySuggestedDecomposition | undefined
        let outcome: CrossClientQueryDecompositionOutcome | null | undefined
        try {
          dispatch(setCrossClientLandingDecompositionResult(null))
          setReviewState(null)
          if (!decomposition) {
            const request = triggerDecomposeCrossClientQuery({
              accountId,
              payload: { userQuestion: args.userQuestion },
            })
            decomposeRequestRef.current = request
            const decomposeResult = await request.unwrap()
            if (!isCurrentRun()) {
              return
            }
            decomposition = decomposeResult.decomposition
            nnfDecomposition = decomposeResult.nnfDecomposition
            suggestion = decomposeResult.suggestedDecomposition ?? undefined
            outcome = decomposeResult.outcome
          }
          dispatch(setCrossClientLandingDecompositionResult({ decomposition, nnfDecomposition }))
        } catch (e) {
          if (!isCurrentRun()) {
            return
          }
          if (isCrossClientFundsQuotaError(e)) {
            dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
          } else {
            console.error(e)
          }
          return
        }

        const rejectionReason = decomposition.rejectionReason?.trim()
        const atomicClauses = decomposition.atomicClauses ?? []
        const isUnusable = isUnusableDecomposition({ outcome, rejectionReason, clauseCount: atomicClauses.length })

        if (didDecompose) {
          decompositionContextRef.current = { outcome: isRecognisedOutcome(outcome) ? outcome : null, hadSuggestion: !!suggestion }
          const event = decompositionEvent({
            outcome,
            suggestion,
            criteriaCount: atomicClauses.length,
            rejectionReason,
            type: args.predefinedQueryId ? 'pre-defined' : 'custom',
            query: args.userQuestion,
          })
          trackingClient.track(event)
        } else if (args.predefinedQueryId) {
          decompositionContextRef.current = { outcome: null, hadSuggestion: false }
        }

        if (isUnusable) {
          const recognisedOutcome = isRecognisedOutcome(outcome) ? outcome : undefined
          setReviewState({
            kind: 'rejected',
            message: rejectionReason || CROSS_CLIENT_DECOMPOSITION_REJECTED_FALLBACK,
            atomicClauses: [],
            sources: args.sources,
            outcome: recognisedOutcome,
            suggestion: recognisedOutcome ? suggestion : undefined,
          })
          return
        }

        if (outcome === CrossClientQueryDecompositionOutcomes.UNDERSPECIFIED && suggestion) {
          setReviewState({
            kind: 'refinement',
            atomicClauses,
            sources: args.sources,
            suggestion,
            decomposition,
            nnfDecomposition,
          })
          return
        }

        setReviewState({
          kind: 'confirm',
          atomicClauses: ccqCriterionClauses({ decomposition, nnfDecomposition }),
          sources: args.sources,
          payload: args.predefinedQueryId
            ? { predefinedQueryId: args.predefinedQueryId, sources: args.sources, clientQueryMode: args.clientQueryMode }
            : { userQuestion: args.userQuestion, decomposition, sources: args.sources, clientQueryMode: args.clientQueryMode, nnfDecomposition },
          confirmed: { type: args.predefinedQueryId ? 'pre-defined' : 'custom', query: args.userQuestion, sharedMode: args.clientQueryMode },
        })
      }),
    [dispatch, accountId, triggerDecomposeCrossClientQuery, trackingClient, withInitLoading],
  )

  const runSelectedDecomposition = useCallback(
    async (
      userQuestion: string,
      decomposition: CrossClientQueryLogicalQuestionDecomposition,
      nnfDecomposition: CrossClientQueryLogicalQuestionNNFDecomposition | undefined,
      sources: CrossClientQueryDataSource[],
      clientQueryMode: CrossClientQueryClientQueryMode,
    ) => {
      await decomposeCrossClientQuery({
        userQuestion,
        decomposition,
        nnfDecomposition,
        sources,
        clientQueryMode,
      })
    },
    [decomposeCrossClientQuery],
  )

  const previewPredefinedQuery = useCallback(
    (predefinedQuery: PredefinedCrossClientQuery, sources: CrossClientQueryDataSource[], clientQueryMode: CrossClientQueryClientQueryMode) => {
      dispatch(setCrossClientLandingDecompositionResult(null))
      // A curated query is a new question, not a continuation of the decomposed one, so its outcome
      // must not ride along into the confirm card's own exit events. Selecting the query has already
      // reported its exit from the previous card by the time this runs.
      decompositionContextRef.current = null
      setReviewState({
        kind: 'confirm',
        atomicClauses: ccqCriterionClauses(predefinedQuery),
        sources,
        payload: { predefinedQueryId: predefinedQuery.id, sources, clientQueryMode },
        confirmed: { type: 'pre-defined', query: predefinedQuery.question, sharedMode: clientQueryMode },
      })
    },
    [dispatch],
  )

  // A curated query's criteria don't depend on sources or clients, so it starts with the pills' current values; a decomposed one keeps its own.
  const confirmStart = useCallback(
    async (settings?: ConfirmStartSettings) => {
      if (reviewState?.kind !== 'confirm') {
        return
      }
      const { payload, atomicClauses, confirmed } = reviewState
      const question = confirmed.query
      if (payload.predefinedQueryId !== undefined && settings !== undefined) {
        confirmedEventRef.current = { ...confirmed, sharedMode: settings.clientQueryMode }
        await startCrossClientQuery({ ...payload, ...settings }, { question, atomicClauses, sources: settings.sources })
        return
      }
      confirmedEventRef.current = confirmed
      await startCrossClientQuery(payload, { question, atomicClauses, sources: reviewState.sources })
    },
    [reviewState, startCrossClientQuery],
  )

  const retryStart = useCallback(async () => {
    if (reviewState?.kind !== 'trigger-failed') {
      return
    }
    const { payload, question, atomicClauses, sources } = reviewState
    await startCrossClientQuery(payload, { question, atomicClauses, sources })
  }, [reviewState, startCrossClientQuery])

  return {
    reviewState,
    startingQuery,
    decomposeCrossClientQuery,
    runSelectedDecomposition,
    previewPredefinedQuery,
    confirmStart,
    retryStart,
    resetExecution,
    cancelDecomposition,
    decompositionContextRef,
  }
}

export const useDeleteOwnCrossClientExecution = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const [triggerDeleteCrossClientExecution, { isLoading: isDeleting }] = useDeleteCrossClientQueryExecutionMutation()

  const deleteOwnExecution = useCallback(
    async (execution: CrossClientQueryExecution): Promise<boolean> => {
      if (execution.createdBy?.id !== currentUser.id) {
        return false
      }
      try {
        await triggerDeleteCrossClientExecution({ accountId, executionId: execution.id }).unwrap()
        dispatch(deleteCrossClientLandingExecution(execution.id))
        return true
      } catch (e) {
        console.error(e)
        dispatch(addToast({ variant: 'error', title: 'Could not delete query', messages: ['Please try again.'] }))
        return false
      }
    },
    [accountId, currentUser.id, triggerDeleteCrossClientExecution, dispatch],
  )

  return { deleteOwnExecution, isDeleting }
}
